import { Link } from "react-router-dom";
import { Hammer, CloudRain, HardHat, Sparkles, Layers, Anvil, Waves, Umbrella, TreePine, AppWindow, ArrowUpRight } from "lucide-react";
import { FadeIn, MaskedLineScroll, Overline } from "@/components/Reveal";

const SERVICES = [
  {
    icon: Hammer,
    title: "Réparation de toiture",
    text: "Une tuile déplacée, une ardoise fissurée, un faitage fragilisé ? Nous réparons votre toiture durablement pour stopper les infiltrations avant qu'elles n'endommagent votre maison.",
    link: "/reparation-toiture-94",
    span: "md:col-span-2",
  },
  {
    icon: CloudRain,
    title: "Fuite de toiture",
    text: "Recherche de fuite précise et réparation à la source : nous identifions l'origine réelle de l'infiltration, pas seulement ses traces visibles.",
    link: "/fuite-toiture-94",
    span: "md:col-span-2",
  },
  {
    icon: HardHat,
    title: "Rénovation de toiture",
    text: "Toiture vieillissante ou fatiguée ? Nous la rénovons pour lui redonner étanchéité, solidité et tenue dans le temps.",
    link: "/renovation-toiture-94",
    span: "",
  },
  {
    icon: Sparkles,
    title: "Nettoyage & démoussage",
    text: "Mousse, lichen et pollution fragilisent vos tuiles. Un nettoyage et démoussage régulier prolonge la vie de votre couverture.",
    link: "/demoussage-toiture-94",
    span: "",
  },
  {
    icon: Layers,
    title: "Réfection de toiture",
    text: "Votre couverture arrive en fin de vie ? Nous procédons à la réfection complète ou partielle de votre toiture pour lui offrir une seconde jeunesse.",
    link: "/refection-toiture-94",
    span: "",
  },
  {
    icon: Anvil,
    title: "Zinguerie",
    text: "Gouttières, chéneaux, noues, abergements de cheminée : une zinguerie soignée est la clé d'une évacuation d'eau sans défaut.",
    link: "/zinguerie-94",
    span: "",
  },
  {
    icon: Waves,
    title: "Réparation de gouttières",
    text: "Gouttière qui déborde, se déforme ou fuit aux joints ? Nous réparons et refaçonnons vos descentes pour protéger vos façades.",
    link: "/gouttiere-94",
    span: "",
  },
  {
    icon: Umbrella,
    title: "Étanchéité toiture",
    text: "Membranes, relevés d'étanchéité, points singuliers : nous traitons les zones sensibles pour une protection complète contre l'eau.",
    link: "/etancheite-toiture-94",
    span: "",
  },
  {
    icon: TreePine,
    title: "Charpente",
    text: "Bois humides, versant qui s'affaisse ? Nous contrôlons la charpente à chaque intervention et réparons les sections localisées.",
    link: "/charpente-94",
    span: "",
  },
  {
    icon: AppWindow,
    title: "Pose de velux",
    text: "Faites entrer la lumière dans vos combles : pose et remplacement de fenêtres de toit, avec une étanchéité soignée tout autour.",
    link: null,
    span: "",
  },
];

export default function Services() {
  return (
    <section id="services" className="bg-bone py-24 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <Overline>Nos services — Couvreur 94</Overline>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
              <MaskedLineScroll>Tous les travaux</MaskedLineScroll>
              <MaskedLineScroll delay={0.12}>de <span className="text-signal">toiture</span></MaskedLineScroll>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <FadeIn delay={0.2}>
              <p className="text-base md:text-lg text-midnight/70 leading-relaxed">
                De la simple réparation de tuiles à la rénovation complète, MSC Toitures prend en charge
                l'ensemble de vos travaux de couverture et de zinguerie dans le Val-de-Marne.
                Chaque intervention commence par un diagnostic clair — et un devis gratuit.
              </p>
            </FadeIn>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-midnight/10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const inner = (
              <div className="group h-full bg-bone border-r border-b border-midnight/10 p-8 flex flex-col justify-between gap-10 hover:bg-white transition-colors duration-300">
                <div className="flex items-start justify-between">
                  <Icon size={26} strokeWidth={1.5} className="text-midnight group-hover:text-signal transition-colors duration-300" />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-steel">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-lg flex items-center gap-2">
                    {s.title}
                    {s.link && <ArrowUpRight size={16} strokeWidth={1.5} className="text-signal opacity-0 group-hover:opacity-100 transition-opacity duration-300" />}
                  </h3>
                  <p className="mt-3 text-sm text-midnight/60 leading-relaxed">{s.text}</p>
                </div>
              </div>
            );
            return (
              <FadeIn key={s.title} delay={(i % 4) * 0.08} className={s.span}>
                {s.link ? (
                  <Link to={s.link} data-testid={`service-card-${i}`} className="block h-full">{inner}</Link>
                ) : (
                  <div data-testid={`service-card-${i}`} className="h-full">{inner}</div>
                )}
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}
